/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { ApiStatus } from '../../../../../ts';
import { CreateAsyncThunk, SkillsSlice } from '../../../../../ts/state';
import * as API from '../../../api/skills';
import { triggerSaveJson } from '../../../util/download';
import { selectAssistantName } from '../../assistants/selectors';
import { selectIsAccessUpdatesEnabled } from '../../features/selectors';
import { selectIsNewIa } from '../../instance/selectors';
import {
  addErrorMessage,
  addMessage,
  ToastStatus
} from '../../messages/actions';
import { getAssistantId } from '../../session/selectors';

const PREPARE_SKILL_DOWNLOAD = 'Preparing Skill download...';
const NO_SKILL_DOWNLOAD = 'Skill could not be downloaded at this time.';

export type ExportSkill = {
  skill_id: string;
  v1Content?: boolean;
  version?: string;
};

/**
 * Exports an skill using the `getSkill()` API.
 * The skill is stringified and the browser triggers a download JSON file.
 * @param {object} params The `skill_id` and `v1Content` parameters
 * @param {string} params.skill_id The skill id to export
 * @param {boolean} params.v1Content If true the workspace is exported
 *
 * @returns {AsyncThunk} The async action
 */
export const exportSkill = createAsyncThunk<
  { skill_id: string },
  ExportSkill,
  CreateAsyncThunk
>('skills/export', async (params, { dispatch, getState }) => {
  try {
    const { skill_id, v1Content = true, version } = params;
    dispatch(
      addMessage({
        message: PREPARE_SKILL_DOWNLOAD,
        status: ToastStatus.SUCCESS,
        timeout: 3000
      })
    );

    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to listVariable
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());

    const skill = await API.getSkill(
      skill_id,
      useNewIAUpdatedAccess,
      assistantId,
      {
        export: true,
        sort: 'stable',
        verbose: false,
        version
      }
    );

    let data = skill;
    if (skill && skill.workspace && v1Content) {
      data = skill.workspace as any;
      data.language = skill.language;
      data.description = skill.description;
    }
    if (skill) {
      const assistantName = selectAssistantName(getState());
      if (assistantName) {
        data.name = `${assistantName}-${skill.type}`;
      } else {
        data.name = skill.name;
      }
    }

    triggerSaveJson(data, `${data.name.replace(/\s/g, '-')}.json`);
    return { skill_id };
  } catch (error) {
    dispatch(addErrorMessage({ ...error, message: NO_SKILL_DOWNLOAD }));
    throw error;
  }
});

const reducer = (builder: ActionReducerMapBuilder<SkillsSlice>) => {
  builder
    .addCase(exportSkill.pending, (state, _action) => {
      state.exportStatus = ApiStatus.LOADING;
      state.exportError = null;
    })
    .addCase(exportSkill.fulfilled, (state, _action) => {
      state.exportStatus = ApiStatus.SUCCEEDED;
      state.exportError = null;
    })
    .addCase(exportSkill.rejected, (state, action) => {
      state.exportStatus = ApiStatus.ERROR;
      state.exportError = action.error;
    });
};

export default reducer;
